import { type BlockNoteEditor, createExtension, getBlockInfoFromSelection } from '@blocknote/core';
import { insertOrUpdateBlockForSlashMenu } from '@blocknote/core/extensions';
import { type BlockTypeSelectItem, createReactBlockSpec } from '@blocknote/react';
import { SquareCheckBigIcon } from 'lucide-react';
import { checklistItemConfig } from 'shared/utils/blocknote-schema-configs';
import { nanoid } from 'shared/utils/nanoid';
import { ChecklistItemRender } from '~/modules/common/blocknote/custom-elements/checklist/checklist-item-render';
import type { CustomBlockNoteEditor, IconType } from '~/modules/common/blocknote/types';
import { cn } from '~/utils/cn';

// A CustomBlockNoteEditor annotation would make customSchema reference itself through this block spec (TS2502 circular type).
// biome-ignore lint/suspicious/noExplicitAny: schema-agnostic editor type; see note above
type AnyBlockNoteEditor = BlockNoteEditor<any, any, any>;

/** Empty item becomes a paragraph, a non-empty item splits into a fresh checklist item below; returns true when it handled the key. */
export const handleChecklistItemEnter = (editor: AnyBlockNoteEditor): boolean => {
  const { blockInfo, selectionEmpty } = editor.transact((tr) => ({
    blockInfo: getBlockInfoFromSelection(tr),
    selectionEmpty: tr.selection.anchor === tr.selection.head,
  }));

  if (!blockInfo.isBlockContainer) return false;
  if (blockInfo.blockContent.node.type.name !== 'checklistItem' || !selectionEmpty) return false;

  if (blockInfo.blockContent.node.childCount === 0) {
    editor.updateBlock(editor.getTextCursorPosition().block, { type: 'paragraph', props: {} });
    return true;
  }

  editor.transact((tr) => {
    tr.deleteSelection();
    const pos = tr.selection.from;
    const info = getBlockInfoFromSelection(tr);
    if (!info.isBlockContainer) return;
    // Empty container attrs give the new block default props.
    tr.split(pos, 2, [
      { type: info.bnBlock.node.type, attrs: {} },
      { type: info.blockContent.node.type, attrs: { checkboxId: nanoid(12) } },
    ]);
  });
  // The browser caret only follows once React renders the new block, so defer; the hop through prevBlock forces a
  // real move, because re-setting the block ProseMirror already reports as current would not sync the DOM selection.
  const { block: newItem, prevBlock } = editor.getTextCursorPosition();
  setTimeout(() => {
    if (prevBlock) editor.setTextCursorPosition(prevBlock, 'end');
    editor.setTextCursorPosition(newItem, 'start');
  }, 0);
  return true;
};

/** Turns the block at the cursor into a checklist item; returns false for blocks without inline content. */
const convertToChecklistItem = (editor: AnyBlockNoteEditor): boolean => {
  const { block } = editor.getTextCursorPosition();
  if (editor.schema.blockSchema[block.type].content !== 'inline') return false;
  editor.updateBlock(block, { type: 'checklistItem', props: { checkboxId: nanoid(12) } });
  return true;
};

const checklistExtensions = createExtension({
  key: 'checklist-item-shortcuts' as const,
  // The default schema keeps BlockNote's own checkListItem, whose shortcuts and input rules match the same keys
  // and text; running first makes them create this block.
  runsBefore: ['check-list-item-shortcuts'],
  keyboardShortcuts: { Enter: ({ editor }) => handleChecklistItemEnter(editor), 'Mod-Shift-9': ({ editor }) => convertToChecklistItem(editor) },
  inputRules: [
    {
      find: /^\s?\[\s*]\s$/,
      replace: () => ({ type: 'checklistItem' as const, props: { checkboxId: nanoid(12) } }),
    },
    {
      find: /^\s?\[[Xx]]\s$/,
      replace: () => ({ type: 'checklistItem' as const, props: { checkboxId: nanoid(12), checked: true } }),
    },
  ],
});

export { checklistItemConfig };

export const checklistItemBlock = createReactBlockSpec(
  checklistItemConfig,
  {
    meta: { isolating: false },
    render: (props) => <ChecklistItemRender {...props} />,
    toExternalHTML: ({ block, contentRef }) => {
      const isChecked = block.props.checked ?? false;
      return (
        <div className="checklist-item" data-checked={isChecked}>
          <div contentEditable={false} className="checklist-checkbox-wrapper">
            <input type="checkbox" checked={isChecked} readOnly data-checkbox-id={block.props.checkboxId} className="checklist-checkbox" />
          </div>
          <p className={cn('checklist-content', isChecked && 'checklist-checked')} ref={contentRef} />
        </div>
      );
    },
  },
  [checklistExtensions],
);

export const getChecklistSlashItem = (editor: CustomBlockNoteEditor) => ({
  title: 'Todos',
  key: 'checklistItem',
  onItemClick: () => {
    insertOrUpdateBlockForSlashMenu(editor, { type: 'checklistItem' as const, props: { checkboxId: nanoid(12) } });
  },
  aliases: ['checklist', 'checkbox', 'todo', 'task', 'check', 'todos'],
  group: 'Basic blocks',
  icon: <SquareCheckBigIcon />,
});

export const insertSideChecklistItem = (): BlockTypeSelectItem & { oneInstanceOnly?: boolean } => ({
  name: 'Todos',
  type: 'checklistItem' as const,
  icon: SquareCheckBigIcon as unknown as IconType,
});
